import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import {
    BookOpen, Package, FlaskConical, ShoppingCart, PlusCircle, Link2,
} from 'lucide-react';
import { useSekce } from '../contexts/SekceContext';
import { REZERVA_LISTY } from '../components/MobilniLista';
import MaterialoveKarty from './MaterialoveKarty';
import ProduktKvalita from './ProduktKvalita';
import { ProvozniNaklady } from '../components/nastaveni/Ciselniky';
import NastaveniModulu, { NastaveniTlacitko } from '../components/layout/NastaveniModulu';

// ⚙ Receptář → Parametry kalkulace (dřív Nastavení → Provozní náklady)
const KODY_KALKULACE = ['ELECOST', 'WATCOST', 'GASCOST', 'LABRATE', 'WORKHOURS', 'MACHLIFE', 'MAINCOST'];

const SECTION_COLOR = '#F59E0B';
const SECTION_BG = 'rgba(245,158,11,0.12)';

/**
 * Záložky celého receptáře, seřazené podle toho, jak věci provozem tečou:
 * co nakupujeme → co z toho vyrábíme → co z toho prodáváme.
 *
 * `strana` říká, která ze dvou stránek záložku obsluhuje. Jsou to zatím dva
 * samostatné soubory (MaterialoveKarty ~1 500 a ProduktKvalita ~3 600 řádků)
 * a slepit je do jednoho by nic nezjednodušilo, jen přesunulo problém.
 * Postup místo toho: sdílený výpočet a hooky, přehledy jako samostatné
 * soubory v `pages/receptar/` (první je Mapování Objedname), obal zůstane tenký.
 */
const ZALOZKY = [
    { id: 'suroviny',   label: 'Suroviny',           Icon: Package,      strana: 'spizirna' },
    { id: 'polotovary', label: 'Polotovary',         Icon: FlaskConical, strana: 'recepty'  },
    { id: 'produkty',   label: 'Produkty',           Icon: ShoppingCart, strana: 'recepty'  },
    { id: 'pridavky',   label: 'Ingredience',           Icon: PlusCircle,   strana: 'recepty'  },
    { id: 'objedname',  label: 'Mapování Objedname', Icon: Link2,        strana: 'recepty' },
];

/** Kam vede proklik na konkrétní položku (`{ typ, id }`) z jiného modulu. */
// Obaly a provozní materiál sem už nepatří — ty řeší Balírna.
const ZALOZKA_PRO_TYP = {
    MAT: 'suroviny',
    SEM: 'polotovary', PRO: 'produkty', ADD: 'pridavky',
};

const stranaZalozky = (id) => ZALOZKY.find(z => z.id === id)?.strana ?? 'spizirna';

/**
 * Receptář — suroviny a recepty pod jednou hlavičkou.
 *
 * Soubor se jmenuje Spizirna.jsx z doby, kdy se tak jmenoval i modul.
 * Přejmenování by protáhlo import v app.jsx i historii; sdílené kusy UI
 * v `components/spizirna/` používá stejně i Balírna, takže ten adresář
 * je na úklid tak jako tak.
 *
 * Rozdělení na dva moduly bylo artefaktem frontendu, ne domény: suroviny
 * odjakživa bydlí na `/api/recepty/suroviny` v `ReceptyController`, a obsluha
 * mezi obojím při zadávání receptu skáče pořád dokola.
 *
 * Obě strany se drží **namontované**, jakmile je obsluha jednou otevřela.
 * Odmontovat receptář při každém nahlédnutí do surovin by znamenalo znovu
 * natáhnout seznam receptů a zahodit rozdělanou práci v editoru.
 */
export default function Spizirna({ user, onNavigate, initialPolozka = null, initialDodavatelId = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Záložky = podstránky modulu podle přístupů (produkt-kvalita.suroviny, …)
    const viditelne = ZALOZKY.filter(z => muze(user, `produkt-kvalita.${z.id}`));
    const [nastaveni, setNastaveni] = useState(false);

    // Proklik zvenčí určuje záložku; jinak se začíná surovinami — jsou začátek
    // řetězce a nejčastější důvod, proč sem obsluha jde mimo editaci receptu.
    // Když k ní přístup není, první dostupná.
    const vychozi = (() => {
        const chtena = ZALOZKA_PRO_TYP[initialPolozka?.typ] ?? 'suroviny';
        return viditelne.some(z => z.id === chtena) ? chtena : (viditelne[0]?.id ?? 'suroviny');
    })();
    const [tab, setTab] = useState(vychozi);
    const [navstivene, setNavstivene] = useState(() => new Set([stranaZalozky(vychozi)]));

    const prepnout = (idZalozky) => {
        setTab(idZalozky);
        setNavstivene(prev => new Set(prev).add(stranaZalozky(idZalozky)));
    };

    const strana = stranaZalozky(tab);
    // Kde jsme i čím se přepíná, říká střed spodní lišty — horní hlavička
    // zrušena, sekce se hlásí jen sem.
    useSekce(isMobile ? { sekce: viditelne, aktivni: tab, onVyber: prepnout } : null);

    return (
        <div data-ladit="receptar" style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            /* Mobil = modul připnutý k výřezu, od hlavičky ke spodní hraně.
               Řetěz `height: 100%` přes <main> (jeho odsazení, safe-area a pár
               flexových prvků) končil kus nad spodní hranou a pod modulem
               zůstával pruh pozadí. Stejně je připnuté i hlavní menu
               (`MobilniMenu`, `position: fixed; inset: 0`) — a to ke spodní
               hraně dosáhne. Připnutí zároveň rovnou dává edge-to-edge, takže
               není potřeba rušit boční odsazení <main> zápornými okraji.
               `zIndex: 0` drží modul pod hlavičkou (z-30) i nad září (z-−1). */
            ...(isMobile
                ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0 }
                : { height: '100%' }),
        }}>
            {nastaveni && (
                <NastaveniModulu nadpis="Nastavení receptáře" barva={SECTION_COLOR} isDark={isDark} onClose={() => setNastaveni(false)}
                    sekce={[{ id: 'kalkulace', nazev: 'Parametry kalkulace', popis: 'energie, práce, stroje', obsah: () => <ProvozniNaklady kody={KODY_KALKULACE} isDark={isDark} barva={SECTION_COLOR}
                        popis="Z těchto hodnot počítá kalkulace receptur náklad energií, práce a strojů." /> }]} />
            )}
            {/* Header — na mobilu ho nese TopBar */}
            {!isMobile && (
                <div className="flex items-center gap-3 mb-4" style={{ flexShrink: 0 }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SECTION_BG }}>
                        <BookOpen size={20} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Receptář</h1>
                        <p className="text-xs" style={{ color: secondaryColor }}>
                            Suroviny, polotovary a produkty — karty, ceníky, receptury a kalkulace
                        </p>
                    </div>
                    {muze(user, 'produkt-kvalita.produkty', 'upravuje') && (
                        <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} popis="Nastavení receptáře" />
                    )}
                </div>
            )}

            {/* Pruh záložek jen na desktopu — na mobilu by ukrojil řádek obsahu
                a duploval by hlavičku. Tam sekce přepíná „Palec" vpravo dole,
                stejně jako v Přehledu. */}
            {!isMobile && (
                <div className="flex gap-1 mb-4" style={{
                    flexShrink: 0,
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                    overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none', msOverflowStyle: 'none',
                }}>
                    {viditelne.map((z, i) => (
                        <button key={z.id} onClick={() => prepnout(z.id)}
                            className="flex items-center px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap"
                            style={{
                                color: tab === z.id ? SECTION_COLOR : secondaryColor,
                                background: 'none', border: 'none',
                                borderBottom: `2px solid ${tab === z.id ? SECTION_COLOR : 'transparent'}`,
                                cursor: 'pointer', flexShrink: 0,
                                // Předěl mezi „co nakupujeme" a „co vyrábíme" — sedm
                                // záložek v jedné řadě se jinak čte jako jeden slepenec.
                                marginLeft: z.strana === 'recepty' && viditelne[i - 1]?.strana === 'spizirna' ? 16 : 0,
                            }}>
                            <z.Icon size={14} style={{ marginRight: 6, opacity: tab === z.id ? 1 : 0.6 }} />
                            {z.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Neaktivní strana se schovává, neodmontovává — jinak by se při
                každém přepnutí znovu tahal seznam receptů i surovin.
                Místo pro Palec se NEubírá tady (obsah jde až ke spodní hraně),
                ale uvnitř scrollujících ploch — viz `rezervaDole`. Kdyby se
                odsadil celý modul, vznikne dole prázdný pruh a zase rámeček. */}
            {navstivene.has('spizirna') && (
                <div style={{
                    display: strana === 'spizirna' ? 'flex' : 'none', flexDirection: 'column',
                    flex: 1, minHeight: 0,
                }}>
                    <MaterialoveKarty
                        vlozena tab={tab} onTab={prepnout}
                        onNavigate={onNavigate}
                        initialPolozka={initialPolozka}
                        initialDodavatelId={initialDodavatelId}
                        rezervaDole={isMobile ? REZERVA_LISTY : null}
                    />
                </div>
            )}

            {navstivene.has('recepty') && (
                <div style={{
                    display: strana === 'recepty' ? 'flex' : 'none', flexDirection: 'column',
                    flex: 1, minHeight: 0,
                    /* Polotovary/Produkty zatím jedou starým rozvržením: edge-to-edge
                       z obalu se jim vrací zpátky (karty se zaoblením na hraně
                       displeje vypadají useknutě) a místo pro Palec se jim musí
                       ubrat zvenčí, dokud nedostanou `mobilRezim`. Ingredience
                       už ho mají (jako Suroviny) — sedí na hranách a Palec si
                       dávají dovnitř scrollu přes `rezervaDole`. */
                    ...(isMobile && tab !== 'pridavky' ? { paddingLeft: 16, paddingRight: 16, paddingBottom: REZERVA_LISTY } : null),
                }}>
                    <ProduktKvalita
                        vlozena tab={tab} onTab={prepnout}
                        user={user} onNavigate={onNavigate}
                        initialPolozka={initialPolozka}
                        rezervaDole={isMobile && tab === 'pridavky' ? REZERVA_LISTY : null}
                    />
                </div>
            )}

        </div>
    );
}
